// App entry (D-026). Builds the semantic shell, wires drag&drop → the canonical pipeline →
// results, and exposes window.__rechnungCheck.validate for the CI parity harness (D-031).
import './styles.css';
import { validate } from './core/pipeline';
import { createDropzone } from './ui/dropzone';
import { renderBusy, renderResult } from './ui/result-view';
import { siteNavHtml } from './ui/site-nav';
import { loadLastResult, saveLastResult } from './ui/session-persistence';
import type { ValidateInput } from './core/types';

function mount(root: HTMLElement): void {
  root.replaceChildren();

  const header = document.createElement('header');
  header.className = 'site-header';
  header.innerHTML = `
    <h1 class="site-title">Rechnungslupe</h1>
    <p class="site-tagline">E-Rechnungen (XRechnung &amp; ZUGFeRD) anzeigen und prüfen —
      <strong>Ihre Rechnung verlässt Ihr Gerät nicht.</strong></p>
    ${siteNavHtml('start')}`;

  const main = document.createElement('main');
  main.className = 'site-main';

  const dropSection = document.createElement('section');
  dropSection.setAttribute('aria-label', 'Rechnung prüfen');

  // aria-live status region: screen readers announce "Prüfe…" and the verdict without a focus move.
  const status = document.createElement('div');
  status.className = 'status-region';
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');

  const results = document.createElement('div');
  results.className = 'results';

  const dropzone = createDropzone((input) => void run(input, status, results));
  dropSection.append(dropzone, status, results);
  main.append(dropSection);

  const footer = document.createElement('footer');
  footer.className = 'site-footer';
  footer.innerHTML = `
    <p>Rechnungslupe erklärt technische Prüfregeln der amtlichen Validierung.
       Es ersetzt keine steuerliche Beratung.</p>`;

  root.append(header, main, footer);

  // Restore the last checked invoice if the visitor just navigated here from another page
  // (Datenschutz/Impressum/So funktioniert's are separate page loads) — see session-persistence.ts.
  const restored = loadLastResult();
  if (restored) renderResult(results, restored);
}

async function run(input: ValidateInput, status: HTMLElement, results: HTMLElement): Promise<void> {
  // Clear any previous verdict immediately so a stale result never lingers while the new file
  // is being checked.
  results.replaceChildren();
  renderBusy(status, input.filename);
  try {
    const result = await validate(input);
    status.replaceChildren();
    renderResult(results, result);
    saveLastResult(result);
  } catch (e) {
    status.replaceChildren();
    const errorResult = {
      verdict: 'undetermined' as const,
      detection: {
        container: 'xml' as const,
        syntax: null,
        docType: 'unknown' as const,
        profileId: null,
        profileClass: 'unknown' as const,
        refereeable: false,
      },
      findings: [],
      ruleIds: [],
      xsdValid: null,
      schematronCrashed: null,
      renderedHtml: null,
      error: String((e as Error)?.message ?? e),
    };
    renderResult(results, errorResult);
    saveLastResult(errorResult);
  }
}

const root = document.getElementById('app');
if (root) mount(root);

// The parity harness drives the exact same pipeline the UI uses (D-031). Same-origin only; no
// network beyond engine assets, so this exposes nothing the app itself doesn't already do.
window.__rechnungCheck = { validate };

// Offline support (BRIEF §5, D-037): precaches the app shell + engines so a second visit — and a
// full validation run — works with no network at all. Same-origin only (dist/sw.js, generated by
// scripts/gen-sw.mjs); production builds only (there is no dist/sw.js in dev).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {
    /* offline support is a progressive enhancement — never block the app on it */
  });
}
